import React, { useEffect, useRef } from 'react';
import './ConfettiBombardment.scss';

/**
 * ConfettiBombardment component for celebrating successful verification
 * Creates a festive confetti explosion effect with multiple colors and animations
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.trigger - Whether to trigger the confetti animation
 * @param {number} props.duration - Animation duration in milliseconds (default: 3000)
 * @param {Function} props.onCompletion - Callback function when animation completes
 *
 * @returns {JSX.Element} Confetti bombardment component
 *
 * @example
 * // Basic usage
 * <ConfettiBombardment trigger={showConfetti} />
 *
 * @example
 * // With custom duration and callback
 * <ConfettiBombardment
 *   trigger={showConfetti}
 *   duration={5000}
 *   onCompletion={() => setShowConfetti(false)}
 * />
 */
export const ConfettiBombardment = ({
  trigger,
  duration = 3000,
  onCompletion,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!trigger || !containerRef.current) return;

    const container = containerRef.current;
    const particles = [];

    // Use existing colors from global variables
    const colors = [
      '#ff4444', // $error
      '#4ade80', // $success
      '#fb923c', // $warning
      '#3b82f6', // $focus-blue
      '#677eea', // $primary-color
      '#fbbf24', // $amber
      '#564d8d', // $muted-purple
      '#a397e6', // $muted-purple-text
    ];

    // Create confetti particles
    for (let i = 0; i < 150; i++) {
      const particle = document.createElement('div');
      particle.className = 'confetti-particle';

      // Random properties
      const color = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() * 8 + 4;
      const left = Math.random() * 100;
      const animationDuration = Math.random() * 2 + 1;
      const delay = Math.random() * 0.5;

      particle.style.cssText = `
        background: ${color};
        width: ${size}px;
        height: ${size}px;
        left: ${left}%;
        animation-duration: ${animationDuration}s;
        animation-delay: ${delay}s;
      `;

      // Random shape (square or circle)
      if (Math.random() > 0.5) {
        particle.style.borderRadius = '50%';
      }

      container.appendChild(particle);
      particles.push(particle);
    }

    // Create firework bursts
    for (let i = 0; i < 8; i++) {
      const firework = document.createElement('div');
      firework.className = 'firework';

      const left = Math.random() * 80 + 10;
      const top = Math.random() * 60 + 20;
      const color = colors[Math.floor(Math.random() * colors.length)];

      firework.style.cssText = `
        left: ${left}%;
        top: ${top}%;
        --firework-color: ${color};
      `;

      container.appendChild(firework);
      particles.push(firework);
    }

    // Create emoji bombs
    const emojis = ['🎉', '🎊', '✨', '🌟', '💫', '🎆', '🎇', '🎈'];
    for (let i = 0; i < 12; i++) {
      const emoji = document.createElement('div');
      emoji.className = 'emoji-bomb';
      emoji.textContent = emojis[Math.floor(Math.random() * emojis.length)];

      const left = Math.random() * 80 + 10;
      const animationDuration = Math.random() * 2 + 2;
      const delay = Math.random() * 1;

      emoji.style.cssText = `
        left: ${left}%;
        animation-duration: ${animationDuration}s;
        animation-delay: ${delay}s;
      `;

      container.appendChild(emoji);
      particles.push(emoji);
    }

    // Create celebration text explosion
    const bombText = document.createElement('div');
    bombText.className = 'bomb-text';
    bombText.textContent = '🎊 BOOM! 🎊';
    container.appendChild(bombText);
    particles.push(bombText);

    // Clean up after animation
    const cleanup = setTimeout(() => {
      particles.forEach((particle) => {
        if (particle.parentNode === container) {
          container.removeChild(particle);
        }
      });

      // Call onCompletion callback to reset trigger
      if (onCompletion && typeof onCompletion === 'function') {
        onCompletion();
      }
    }, duration + 1000);

    return () => {
      clearTimeout(cleanup);
      // Cleanup particles on unmount to prevent memory leak
      particles.forEach((particle) => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      });
    };
  }, [trigger, duration, onCompletion]);

  return <div ref={containerRef} className='confetti-bombardment' />;
};

export default ConfettiBombardment;
